import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  TableSortLabel,
  TextField,
  Modal,
  Grid,
  Box,
  Popover,
  IconButton,
  Chip, Link,
  InputAdornment,
  Divider
} from "@mui/material";
import {ArrowForwardRounded, Close, AccountBoxRounded, BusinessRounded, CheckCircleRounded, CancelRounded, EmailRounded, PhoneRounded, MoreVert, InfoRounded, SearchRounded, CloseRounded} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO, format } from "date-fns";
import { DashboardContainer, MobileCard, DesktopTable } from "./styles";
import { useDealer } from "context/dealer/dealerContext";
import {useAuth} from "context/authContext";
import {useUI} from "context/ui/uiContext";
import {useIsMobile} from "hooks/useIsMobile";
import {AccountDLQ} from "components/dealer/modals/AccountDLQ";
import {PickupAlert} from "components/dealer/modals/PickupAlert";
import {Inelegible, Ineligible} from "components/dealer/modals/Ineligible";
/**
 * DealerDashboard is a component that displays a list of vehicles from the dealer's inventory.
 * It provides functionalities such as viewing vehicle details, initiating inspections,
 * and sorting vehicles by various attributes like year, make, model, and VIN.
 *
 * The component includes a date range picker to filter vehicles based on a specified date range,
 * and it automatically fetches and updates vehicle data based on the selected date range.
 * Additionally, the component displays a loading indicator while fetching data and
 * allows for sorting the displayed vehicles in ascending or descending order by clicking on table headers.
 * returns (
 *   <DealerDashboard />
 * )
 */
function DealerDashboard() {
  const { fetchDealerVehicles,
    dealerVehicles, fetchVehicleDetails, filterVehicles, submitDealerOfferDecision,
    startDate, setStartDate, endDate, setEndDate, selectedRange, setSelectedRange} = useDealer();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [vin, setVin] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const { showLoadingSpinner, hideLoadingSpinner } = useUI();
  // Offer Modal
  const [offerModalOpen, setOfferModalOpen] = useState(false); // State to manage offer modal
  const [offerDetails, setOfferDetails] = useState({}); // State to store offer details

  /** Title holding state */
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [isDLQModalOpen, setIsDLQModalOpen] = useState(false);
  const [titlePossessionAnswer, setTitlePossessionAnswer] = useState(null);
  const [isPickupModalOpen, setIsPickupodalOpen] = useState(false);
  const [isIneligible, setIsIneligible] = useState(false);
  const isMobile = useIsMobile();

  const handleDateRangeSelection = (days) => {
    const end = new Date();
    const start = new Date(new Date().setDate(new Date().getDate() - days));
    setStartDate(start);
    setEndDate(end);
    setSelectedRange(days);  // This will save to localStorage automatically
  };

  const handleStartDateChange = (newDate) => {
    setStartDate(newDate); // Automatically persists to localStorage
    setEndDate(endDate);   // Ensure endDate is set to update localStorage correctly
  };

  const handleEndDateChange = (newDate) => {
    setEndDate(newDate);   // Automatically persists to localStorage
    setStartDate(startDate); // Ensure startDate is set to update localStorage correctly
  };
  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Function to handle sorting
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Sort vehicles
  const sortedVehicles = React.useMemo(() => {
    if (!dealerVehicles.length) {
      return [];
    }

    let sortableItems = [...dealerVehicles];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [dealerVehicles, sortConfig]);

  const openDetailsModal = async ({vehicleID}) => {
    setOpenModal(true);
    const vehicle_details = await fetchVehicleDetails({vehicleID});
    setVehicleDetails(vehicle_details);
    console.log(vehicle_details);
  };

  const closeDetailsModal = () => {
    setOpenModal(null);
    setVehicleDetails(null);
  };

  const handleOpenMoreInfo = (event, vehicle) => {
    setAnchorEl(event.currentTarget);
    setSelectedVehicle(vehicle);
  };

  const handleCloseMoreInfo = () => {
    setAnchorEl(null);
    setSelectedVehicle(null);
  };



  const handleSearchByVin = async () => {
    if (vin.length < 6 || vin.length > 17) {
      setError("VIN must be between 6 and 17 characters.");
      return;
    }
    try {
      console.log("searching");
      await filterVehicles({ vin });
    }
    catch (error) {
      console.log(error);
    }
  };

  // Handle input change
  const handleVinChange = (e) => {
    setVin(e.target.value);
    setError(null); // Clear error when typing
  };

  // Handle clearing the search input
  const clearVin = () => {
    setVin("");
    fetchDealerVehicles();
  };

  // Open offer modal
  const handleOfferClick = (vehicle) => {
    setOfferDetails({
      offerAmount: vehicle.offerAmount,
      offerDate: vehicle.offerDate,
      offerExpirationDate: vehicle.offerExpirationDate,
      vehicleID: vehicle.vehicleId
    });
    setOfferModalOpen(true);
  };

  // Close offer modal
  const closeOfferModal = () => {
    setOfferModalOpen(false);
    setOfferDetails({});
  };

  // Accept and decline button handlers
  const handleAcceptOffer = async () => {
    console.log("Offer Accepted");
    closeOfferModal();

    showLoadingSpinner();
    const {vehicleID} = offerDetails;
    await submitDealerOfferDecision({vehicleID, offerAccepted: true});
    hideLoadingSpinner();
  };

  const handleBeginInspection = (vehicle) => {
    // Check if the vehicle is in a title-holding state

    if (vehicle.isReturnEligible === false) {
      setIsIneligible(true);
      return;
    }

    if (vehicle?.isPickup) {
      setIsPickupodalOpen(true);
      return;
    }

    if (vehicle?.isAccountDelinquent) {
      setIsDLQModalOpen(true);
      return;
    }

    if (vehicle?.isTitleHoldingState) {
      setIsTitleModalOpen(true); // Open the title possession modal
    } else {
      navigate(`/dealer/inspection/condition/${vehicle.vehicleId}`);
    }
  };

  const resetTitleModal = () => {
    setTitlePossessionAnswer(null);
    setIsTitleModalOpen(false);
  };

  const handleDeclineOffer = async () => {
    console.log("Offer Declined");
    closeOfferModal();

    showLoadingSpinner();
    const {vehicleID} = offerDetails;
    await submitDealerOfferDecision({vehicleID, offerAccepted: false});
    hideLoadingSpinner();
  };


  useEffect(() => {
    fetchDealerVehicles(format(startDate, "yyyy-MM-dd"), format(endDate, "yyyy-MM-dd"));
  }, [startDate, endDate]);

  const getStatusColor = (status) => {
    switch (status) {
    case "Pending Inspection":
    case "Inbound":
      return "primary";
    case "Inspected":
      return "success";
    case "Offer Accepted":
      return "success";
    case "Repaired":
      return "info";
    case "Out of Service":
      return "secondary";
    default:
      return "warning"; // Default color for unknown statuses
    }
  };

  // Loading indicator
  if (isLoading) {
    return (
      <DashboardContainer>
        <CircularProgress />
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Typography variant="h4" gutterBottom>
        Turn-in Manager
      </Typography>
      <Grid spacing={4} container>
        {/* Data Table */}
        <Grid item xs={8}>
          <DesktopTable>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "year"}
                      direction={sortConfig.key === "year" ? sortConfig.direction : "asc"}
                      onClick={() => requestSort("year")}
                    >
                      Year
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "model"}
                      direction={sortConfig.key === "model" ? sortConfig.direction : "asc"}
                      onClick={() => requestSort("model")}
                    >
                      Model
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "vin"}
                      direction={sortConfig.key === "vin" ? sortConfig.direction : "asc"}
                      onClick={() => requestSort("vin")}
                    >
                      VIN
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === "status"}
                      direction={sortConfig.key === "status" ? sortConfig.direction : "asc"}
                      onClick={() => requestSort("status")}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>&nbsp;</TableCell> {/* Placeholder for More Options */}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedVehicles.map((vehicle, index) => (
                  <TableRow key={index}>
                    <TableCell>{vehicle.year}</TableCell>
                    <TableCell>{vehicle.model}</TableCell>
                    <TableCell>{vehicle.vin}</TableCell>
                    <TableCell>{vehicle.status === "Offer Available" ? (
                      <Chip
                        label="Offer Available"
                        color="success"
                        icon={<ArrowForwardRounded />} // Icon to indicate it's clickable
                        onClick={() => handleOfferClick(vehicle)} // Click handler
                        sx={{ marginLeft: 2, color: "white", cursor: "pointer" }} // Style changes for clickable button
                      />
                    ) : (
                      <Chip
                        label={vehicle?.status}
                        color={getStatusColor(vehicle?.status)}
                        sx={{ marginLeft: 2, color: "white" }}
                      >
                        {vehicle?.status}
                      </Chip>
                    )}</TableCell>
                    <TableCell>
                      <IconButton onClick={(event) => handleOpenMoreInfo(event, vehicle)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Grid item xs={12}>
              {sortedVehicles.length < 1 && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ borderRadius: "8px" }} // Full width
                >
                  <Typography variant="body1" gutterBottom>
                    No units found. Try changing the date range or searching by VIN.
                  </Typography>
                </Alert>
              )}
            </Grid>
          </DesktopTable>

        </Grid>



        {/* Right Column */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={4}>  {/* Container with spacing for uniform gap */}
            {/* First Paper Box */}
            <Grid item xs={12}>
              <Card elevation={3} style={{ padding: 10 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom style={{ paddingBottom: 10 }}>
                    Filter
                  </Typography>
                  <Grid container spacing={2}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Grid item xs={6}>
                        <DatePicker
                          size="small"
                          label="Start Date"
                          value={startDate}
                          onChange={handleStartDateChange}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              InputProps={{
                                ...params.InputProps,
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker
                          size="small"
                          label="End Date"
                          value={endDate}
                          onChange={handleEndDateChange}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </Grid>
                    </LocalizationProvider>

                    {/* Date Range Chips */}
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        <Grid item>
                          <Chip
                            label="7 Days"
                            color="primary"
                            onClick={() => handleDateRangeSelection(7)}
                            variant={selectedRange === 7 ? "filled" : "outlined"} // Highlight selected
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            label="30 Days"
                            color="secondary"
                            onClick={() => handleDateRangeSelection(30)}
                            variant={selectedRange === 30 ? "filled" : "outlined"} // Highlight selected
                          />
                        </Grid>
                        <Grid item>
                          <Chip
                            label="90 Days"
                            color="default"
                            onClick={() => handleDateRangeSelection(90)}
                            variant={selectedRange === 90 ? "filled" : "outlined"} // Highlight selected
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Search by VIN"
                        variant="outlined"
                        fullWidth
                        value={vin}
                        onChange={handleVinChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={clearVin}>
                                <CloseRounded />
                              </IconButton>
                            </InputAdornment>
                          ),
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchRounded />
                            </InputAdornment>
                          ),
                        }}
                        error={!!error}
                        helperText={error}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearchByVin}
                        fullWidth
                      >
                        Search
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Second Paper Box */}
            <Grid item xs={12}>
              <Card elevation={3} style={{ padding: 10 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Dealer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <AccountBoxRounded />
                        </Grid>
                        <Grid item xs>
                          <Typography variant="body1">
                            {currentUser?.username}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <BusinessRounded />
                        </Grid>
                        <Grid item xs>
                          <Typography variant="body1">
                            {currentUser?.name}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <EmailRounded />
                        </Grid>
                        <Grid item xs>
                          <Typography variant="body1">
                            {currentUser?.email}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <PhoneRounded />
                        </Grid>
                        <Grid item xs>
                          <Typography variant="body1">
                            {currentUser?.phone}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <AccountBoxRounded fontSize="medium" />
                    </Grid>
                    <Grid item>
                      <Typography variant="h6" gutterBottom>
                        Help
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="body1" gutterBottom>
                    For questions regarding a customer’s HDFS loan, end of loan options, or for technical assistance, contact our Flex Financing team at <Link href="tel:800-717-3450" underline="hover">800-717-3450</Link>.
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    For questions regarding the title or title process, contact the Titles Department at <Link href="tel:866-433-7562" underline="hover">866-433-7562</Link>
                  </Typography>

                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseMoreInfo}
          sx={{
            width: 500,
          }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <CardContent style={{width: 250}}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2">{selectedVehicle?.year}</Typography>
                <Typography variant="body2">{selectedVehicle?.brand}</Typography>
                <Typography variant="body2">{selectedVehicle?.model}</Typography>
              </Grid>
              {selectedVehicle?.receivingDate && <Grid item xs={12}>
                <Typography variant="body2"><strong>Received:</strong> {selectedVehicle?.receivingDate && format(parseISO(selectedVehicle?.receivingDate), "MM-dd-yyyy (HH:mm)")}</Typography>
              </Grid>}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<SearchRounded />}
                  onClick={() => handleBeginInspection(selectedVehicle)}
                  fullWidth
                  disabled={
                    !(
                      selectedVehicle?.canDoInspectionState || // Always enable if this is true
                      selectedVehicle?.isPickup || // Enable if isPickup is true
                      selectedVehicle?.isReturnEligible === false ||
                      (selectedVehicle?.isAccountDelinquent && selectedVehicle?.canDoInspectionState === false) // Enable if isAccountDelinquent is true and canDoInspectionState is false
                    )
                  }
                >
                  Begin Inspection
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<InfoRounded />}
                  onClick={() => openDetailsModal({ vehicleID: selectedVehicle?.vehicleId })}
                  fullWidth
                >
                  History
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Popover>
      </Grid>

      {/* Card Layout for Mobile Screens */}
      {isMobile && <Box sx={{marginTop: 3}}><Typography variant="h5">
        Vehicles
      </Typography></Box>}
      {sortedVehicles.map((vehicle, index) => (
        <MobileCard key={index}>
          <CardContent>
            <Typography variant="h6" component="div">
              {vehicle.year} {vehicle.brand} {vehicle.model}
            </Typography>
            <Typography variant="body2" color="textSecondary" component="p">
              VIN: {vehicle.vin}
            </Typography>
            <Typography variant="body2" color="textSecondary" component="p">
              Status: {vehicle.status}
            </Typography>
            <Button onClick={(event) => handleOpenMoreInfo(event, vehicle)} variant="contained" color="secondary" style={{ marginTop: "10px" }}>
              Details
            </Button>
          </CardContent>
        </MobileCard>
      ))}

      <Modal
        open={openModal}
        onClose={closeDetailsModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? 200 : 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="close"
            onClick={closeDetailsModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseRounded />
          </IconButton>

          {vehicleDetails ? (
            <>
              <Typography id="modal-modal-title" variant="h5" component="h2" sx={{ fontWeight: "bold", mb: 2 }}>
                {vehicleDetails.year} {vehicleDetails.brand} {vehicleDetails.model}
              </Typography>
              <Box sx={{ borderTop: "1px solid #ddd", mt: 2, pt: 2 }}>
                {vehicleDetails.eventHistories.map((item, i) => (
                  <Box
                    key={`history_${i}`}
                    sx={{
                      mb: 3,
                      p: 2,
                      border: "1px solid #ccc", // Light border for each item
                      borderRadius: 2, // Rounded corners
                      boxShadow: 1, // Subtle shadow for depth
                      bgcolor: "background.paper", // Ensure consistent background
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {item?.eventDate && format(parseISO(item.eventDate), "MM-dd-yyyy (HH:mm)")}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: "medium" }}>
                      Event: {item?.eventType}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Title: {item?.eventDescription}
                    </Typography>
                  </Box>
                ))}
              </Box>

            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Modal>

      {/* Offer Modal */}
      <Modal
        open={offerModalOpen}
        onClose={closeOfferModal}
        aria-labelledby="offer-modal-title"
        aria-describedby="offer-modal-description"
      >
        <Box
          sx={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxWidth: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            position: "relative" // To allow absolute positioning of the close button
          }}
        >
          {/* Close (X) Button */}
          <IconButton
            aria-label="close"
            onClick={closeOfferModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          >
            <Close />
          </IconButton>
          <Typography
            id="offer-modal-title"
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", mb: 2 }}
          >
            <InfoRounded sx={{ mr: 1 }} />
            Offer Details
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: "bold", flex: 1 }}>
                Offer Amount:
              </Typography>
              <Typography variant="body1" color="primary" sx={{ fontWeight: "bold" }}>
                {offerDetails.offerAmount ? `$${offerDetails.offerAmount.toLocaleString("en-US")}` : "N/A"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: "bold", flex: 1 }}>
                Offer Date:
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {offerDetails?.offerDate ? format(parseISO(offerDetails?.offerDate), "MM-dd-yyyy") : "N/A"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: "bold", flex: 1 }}>
                Expires:
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {offerDetails?.offerExpirationDate ? format(parseISO(offerDetails?.offerExpirationDate), "MM-dd-yyyy") : "N/A"}
              </Typography>
            </Box>


          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAcceptOffer}
              startIcon={<CheckCircleRounded />}
              sx={{ flex: 1 }}
            >
              Accept
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDeclineOffer}
              startIcon={<CancelRounded />}
              sx={{ flex: 1 }}
            >
              Decline
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Title Possession Modal */}
      <Modal
        open={isTitleModalOpen}
        onClose={resetTitleModal}  // Reset state when modal is closed
        aria-labelledby="title-possession-modal-title"
        aria-describedby="title-possession-modal-description"
      >
        <Box
          sx={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            position: "relative"
          }}
        >
          <IconButton
            aria-label="close"
            onClick={resetTitleModal}  // Close and reset modal
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          >
            <Close />
          </IconButton>

          <Typography
            id="title-possession-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            Is the title in possession of a dealer representative?
          </Typography>

          {titlePossessionAnswer === null && (
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setTitlePossessionAnswer("yes")}
              >
                Yes
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setTitlePossessionAnswer("no")}
              >
                No
              </Button>
            </Box>
          )}

          {titlePossessionAnswer === "yes" && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" gutterBottom>
                Please overnight the title within 3 business days to satisfy title requirements for turn-in to the following address:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
                Harley Davidson Credit Corp
              </Typography>
              <Typography variant="body2">ATTN: Titles</Typography>
              <Typography variant="body2">9850 Double R Blvd. Ste 100</Typography>
              <Typography variant="body2">Reno, NV 89521</Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  resetTitleModal();  // Reset modal state
                  navigate(`/dealer/inspection/condition/${selectedVehicle?.vehicleId}`);
                }}
                sx={{ mt: 2 }}
              >
                Begin Inspection
              </Button>
            </Box>
          )}

          {titlePossessionAnswer === "no" && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body1" color="error" gutterBottom>
                Title must be in possession of a dealership representative to proceed with motorcycle turn-in.
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                onClick={resetTitleModal}  // Reset and close modal
                sx={{ mt: 2 }}
              >
                Close
              </Button>
            </Box>
          )}
        </Box>
      </Modal>

      <AccountDLQ show={isDLQModalOpen} onClose={() => setIsDLQModalOpen(false)} />
      <PickupAlert show={isPickupModalOpen} onClose={() => setIsPickupodalOpen(false)} />
      <Ineligible show={isIneligible} onClose={() => setIsIneligible(false)} />
    </DashboardContainer>
  );
}

export default DealerDashboard;
